/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import { Alert, Heading, HFlow, Icon, isEqual, ModalCloseButton, Status, Text, Theme, useTheme, VFlow } from 'bold-ui'
import { AccordionButton } from 'components/accordion/AccordionButton'
import { Box } from 'components/Box'
import { resolveName } from 'components/form'
import { InfoIcon } from 'components/InfoIcon'
import { MChatPerguntaEnum, MChatRiscoEnum } from 'graphql/types.generated'
import { useEffect, useState } from 'react'
import { Accordion, AccordionItem, AccordionItemPanel } from 'react-accessible-accordion'
import { useField } from 'react-final-form'
import { MetaRoot } from 'util/metaPath'

import { MCHAT_PERGUNTAS } from '../../mchat/model-mchat'
import { ConsultaSeguimentoPerguntaSelectList } from '../components/ConsultaSeguimentoPerguntaSelectList'
import { ConsultaSeguimentoPerguntaSelectListField } from '../components/ConsultaSeguimentoPerguntaSelectListField'
import { hasAnyPerguntaData, hasPerguntaData } from '../consulta-seguimento-aplicacao/utils-consultaSeguimentoAplicacao'
import {
  ConsultaSeguimentoAplicacaoModel,
  ConsultaSeguimentoFieldModel,
  ConsultaSeguimentoSelectionState,
} from '../model-consultaSeguimento'
import { getPerguntasSelecionadasFieldHasError } from '../utils-consultaSeguimento'

interface ConsultaSeguimentoModalTabSelecaoProps {
  path: MetaRoot<ConsultaSeguimentoFieldModel>
  mChatRisco: MChatRiscoEnum
  mChatPerguntasFalhas: MChatPerguntaEnum[]
  selectionState: ConsultaSeguimentoSelectionState
  clearStartFailed: () => void
}

const LIST_OF_ALL_PERGUNTAS = Object.keys(MCHAT_PERGUNTAS) as MChatPerguntaEnum[]

export function ConsultaSeguimentoModalTabSelecao(props: Readonly<ConsultaSeguimentoModalTabSelecaoProps>) {
  const { path, mChatRisco, mChatPerguntasFalhas = [], selectionState, clearStartFailed } = props

  const theme = useTheme()
  const styles = createStyles(theme)

  const {
    input: { value: perguntasSelecionadas },
    meta,
  } = useField<MChatPerguntaEnum[]>(resolveName(path.perguntasSelecionadas), {
    subscription: { value: true, error: true, touched: true },
  })

  const {
    input: { value: aplicacao },
  } = useField<ConsultaSeguimentoAplicacaoModel>(resolveName(path.aplicacao), {
    subscription: { value: true },
  })

  const isModifiedSinceLastSubmit = !isEqual(perguntasSelecionadas || [], selectionState.values)
  const startFailed = selectionState.failed

  const hasPerguntasFalhasNoMChat = mChatPerguntasFalhas.length > 0
  const isMChatRiscoBaixo = mChatRisco === MChatRiscoEnum.BAIXO

  const listOfPerguntasNaoFalhas = LIST_OF_ALL_PERGUNTAS.filter((item) => !mChatPerguntasFalhas.includes(item))
  const isSomePerguntaNaoFalhaSelecionada = (perguntasSelecionadas || []).some((item) =>
    listOfPerguntasNaoFalhas.includes(item)
  )

  const hasError = getPerguntasSelecionadasFieldHasError(meta, isMChatRiscoBaixo)
  const hasRespostas = hasAnyPerguntaData(aplicacao || {})

  const [showAlertError, setShowAlertError] = useState(startFailed)

  const isAccordionPreExpanded = isMChatRiscoBaixo || isSomePerguntaNaoFalhaSelecionada

  const closeAlertError = () => {
    setShowAlertError(false)
    clearStartFailed()
  }

  const getPerguntaFromSelectListHasRespostas = (pergunta: MChatPerguntaEnum): boolean => {
    return hasPerguntaData(pergunta, aplicacao || {})
  }

  useEffect(() => {
    if (startFailed) {
      setShowAlertError(true)
    }
  }, [startFailed])

  useEffect(() => {
    if (!hasError) {
      setShowAlertError(false)
    }
  }, [hasError])

  useEffect(() => {
    if (isModifiedSinceLastSubmit && startFailed && !hasError) {
      clearStartFailed()
    }
  }, [clearStartFailed, isModifiedSinceLastSubmit, hasError, startFailed])

  return (
    <VFlow vSpacing={2}>
      <HFlow alignItems='flex-start' justifyContent='flex-end'>
        <ModalCloseButton />
      </HFlow>
      <Heading level={1}>Entrevista de seguimento</Heading>
      <VFlow>
        <Text>
          <strong>
            Na Entrevista de Seguimento é necessário reaplicar as perguntas nas quais a criança inicialmente falhou no
            M-Chat-R.
          </strong>{' '}
          Cada página da entrevista corresponde a uma pergunta do M-CHAT-R.{' '}
        </Text>

        {hasRespostas && (
          <Alert type='info' inline styles={{ wrapper: styles.alertWrapper, container: styles.alertContainer }}>
            <strong>O preenchimento já foi iniciado:</strong> é possível adicionar novas perguntas para aplicação. As
            respostas já registradas serão mantidas, a não ser que a pergunta seja removida na alteração.
          </Alert>
        )}

        {!hasPerguntasFalhasNoMChat && (
          <Alert type='warning' inline styles={{ wrapper: styles.alertWrapper, container: styles.alertContainer }}>
            A entrevista de seguimento é recomendada para resultados dentro do Risco Médio no M-CHAT-R.
          </Alert>
        )}

        {showAlertError && (
          <Alert
            type='danger'
            inline
            styles={{ wrapper: styles.alertWrapper, container: styles.alertContainer }}
            onCloseClick={closeAlertError}
          >
            É necessário selecionar pelo menos 3 perguntas para iniciar a Entrevista de Seguimento.
          </Alert>
        )}

        {hasPerguntasFalhasNoMChat && (
          <VFlow>
            <Box style={styles.box()}>
              <VFlow vSpacing={0}>
                <HFlow alignItems='center' hSpacing={0.5} style={styles.header}>
                  <Text fontWeight='bold'>Perguntas selecionadas automaticamente</Text>
                  <InfoIcon
                    icon='infoCircleFilled'
                    text='As perguntas nas quais a criança falhou no M-CHAT-R foram selecionadas automaticamente.'
                  />
                </HFlow>
                <ConsultaSeguimentoPerguntaSelectList
                  listOfPerguntas={mChatPerguntasFalhas}
                  disabled
                  disabledTooltipText='Perguntas nas quais a criança falhou são obrigatórias'
                  getListItemHasData={getPerguntaFromSelectListHasRespostas}
                />
              </VFlow>
            </Box>

            <Box style={styles.box(hasError)}>
              <Accordion
                allowZeroExpanded={!isMChatRiscoBaixo}
                preExpanded={isAccordionPreExpanded ? ['select-more-questions'] : []}
              >
                <AccordionItem uuid='select-more-questions'>
                  <AccordionButton
                    style={styles.accordionButton}
                    wrapperStyle={styles.accordionButtonWrapper}
                    alwaysOpen={isMChatRiscoBaixo}
                  >
                    <HFlow alignItems='center' hSpacing={0.625}>
                      <Icon icon='angleDown' />
                      <HFlow hSpacing={0.5} alignItems='center'>
                        <Text fontWeight='bold'>Adicionar outras perguntas do instrumento</Text>
                        {!isMChatRiscoBaixo && <Status type='info' text='Opcional' />}
                      </HFlow>
                    </HFlow>
                  </AccordionButton>
                  <AccordionItemPanel css={styles.accordionPanel}>
                    <ConsultaSeguimentoPerguntaSelectListField
                      name={path.perguntasSelecionadas}
                      listOfPerguntas={listOfPerguntasNaoFalhas}
                      getListItemHasData={getPerguntaFromSelectListHasRespostas}
                    />
                  </AccordionItemPanel>
                </AccordionItem>
              </Accordion>
            </Box>
          </VFlow>
        )}

        {!hasPerguntasFalhasNoMChat && (
          <Box style={styles.box(hasError)}>
            <VFlow vSpacing={0}>
              <HFlow style={styles.header}>
                <Text fontWeight='bold'>Selecione no mínimo 3 perguntas do M-CHAT-R em que a criança falhou</Text>
              </HFlow>
              <ConsultaSeguimentoPerguntaSelectListField
                name={path.perguntasSelecionadas}
                listOfPerguntas={LIST_OF_ALL_PERGUNTAS}
                getListItemHasData={getPerguntaFromSelectListHasRespostas}
              />
            </VFlow>
          </Box>
        )}
      </VFlow>
    </VFlow>
  )
}

const createStyles = (theme: Theme) => ({
  alertWrapper: css`
    width: 100%;
    padding: 0.125rem 0.5rem;
  `,
  alertContainer: css`
    align-items: center;
  `,
  box: (hasError: boolean = false) => css`
    padding: 0;
    border-color: ${hasError ? theme.pallete.status.danger.main : theme.pallete.divider};
  `,
  header: css`
    padding: 0.5rem 1rem;
    border-bottom: 1px solid ${theme.pallete.divider};
  `,
  accordionButton: css`
    margin: 0;
    padding: 0.25rem 0.75rem;
  `,
  accordionButtonWrapper: css`
    padding: 0;
  `,
  accordionPanel: css`
    border-top: 1px solid ${theme.pallete.divider};
  `,
})
